import { computed, onMounted, ref } from 'vue';
import { isEmpty, pick } from 'lodash';
import md5 from 'md5';
import { useAcl, useApiFieldErrors, useI18n, useMixpanel, usePlanLimits, useReportForm, useReportUtils, useTutorialIntro, useTutorialApi, type PlanLimitsProp } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  getAffiliateNetworkApiRequest,
  postCreateAffiliateNetworkApiRequest,
  putUpdateAffiliateNetworkApiRequest,
} from '@/services/api/affiliate-networks-api';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import { useStore } from '@/store';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast';

export type AffiliateNetworkDialogMode = 'create' | 'edit' | 'duplicate';

export type AffiliateNetworkDialogData = {
  mode?: AffiliateNetworkDialogMode;
  id?: string;
  name?: string;
  tags?: unknown[];
  [key: string]: unknown;
};

type AffiliateNetworkForm = {
  postbackStatusDefault: string;
  status: string;
  tags: unknown[];
  clickIdPlaceholder: string;
  payoutPlaceholder: string;
  transactionIdPlaceholder: string;
  statusPlaceholder: string;
  name?: string;
  acceptDoublePostbacks?: boolean;
  appendClickId?: boolean;
  clickIdParam?: string;
  postbackOnlyWhiteIps?: boolean;
  hasCustomParamMasking?: boolean;
  postbackStatusChargeback?: string;
  postbackStatusConfirm?: string;
  postbackStatusDecline?: string;
  postbackStatusNew?: string;
  postbackWhiteIps?: string;
  usePostbackStatuses?: boolean;
  workspaceId?: string;
  templateId?: string;
  created?: string;
  updated?: string;
  updatedBy?: string;
};

const EDIT_AFFILIATE_NETWORK_FIELDS = [
  'name',
  'tags',
  'status',
  'acceptDoublePostbacks',
  'appendClickId',
  'clickIdParam',
  'postbackOnlyWhiteIps',
  'hasCustomParamMasking',
  'postbackStatusChargeback',
  'postbackStatusConfirm',
  'postbackStatusDecline',
  'postbackStatusDefault',
  'postbackStatusNew',
  'postbackWhiteIps',
  'usePostbackStatuses',
  'workspaceId',
  'created',
  'updated',
  'updatedBy',
] as const;

const DUPLICATE_AFFILIATE_NETWORK_FIELDS = [
  'name',
  'tags',
  'status',
  'acceptDoublePostbacks',
  'appendClickId',
  'clickIdParam',
  'postbackOnlyWhiteIps',
  'hasCustomParamMasking',
  'postbackStatusChargeback',
  'postbackStatusConfirm',
  'postbackStatusDecline',
  'postbackStatusDefault',
  'postbackStatusNew',
  'postbackWhiteIps',
  'usePostbackStatuses',
  'workspaceId',
] as const;

const defaultAffiliateNetworkTemplate = () => ({
  clickIdPlaceholder: 'REPLACE',
  payoutPlaceholder: 'OPTIONAL',
  transactionIdPlaceholder: 'OPTIONAL',
  statusPlaceholder: 'OPTIONAL',
});

const defaultForm = (): AffiliateNetworkForm => ({
  postbackStatusDefault: 'confirm',
  status: 'active',
  tags: [],
  ...defaultAffiliateNetworkTemplate(),
});

export const useAffiliateNetworkDialog = (
  mode: AffiliateNetworkDialogMode,
  getData: () => AffiliateNetworkDialogData,
  emitClose: () => void,
) => {
  const store = useStore();
  const { t } = useI18n();
  const { aclReadOnly } = useAcl();
  const { trackMixpanel } = useMixpanel();
  const planLimitsControl = usePlanLimits();

  const edit = ref(mode === 'edit');
  const show = ref(false);
  const step = ref(1);
  const submitLoading = ref(false);
  const affiliateNetworkId = ref('');
  const oldHash = ref('');
  const form = ref<AffiliateNetworkForm>(defaultForm());

  const reportForm = useReportForm(() => ({
    edit: edit.value,
    mode: edit.value ? 'edit' : 'create',
  }));

  const introStepsDefault = computed(() => ({
    2: `<div class='introjs-tooltip__title'>${t('Affiliate Network Settings')}</div><div class='introjs-tooltip__content'>${t('Specify name of affiliate network')}</div>`,
    3: `<div class='introjs-tooltip__title'>${t('Affiliate Network Settings')}</div><div class='introjs-tooltip__content'>${t('Create affiliate network element')}</div>`,
  }));

  const tutorialIntro = useTutorialIntro(() => ({
    introStepsDefault: introStepsDefault.value,
    quickStartSection: 'affiliate-networks',
  }));

  const tutorial = useTutorialApi();

  const {
    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  } = useApiFieldErrors();

  const reportUtilsApi = useReportUtils({});

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const modalTitle = computed(() => {
    if (mode === 'edit') return t('Edit affiliate network');
    if (mode === 'duplicate') return t('Duplicate affiliate network');
    return t('Create affiliate network');
  });

  const workspacesList = computed(() => reportForm.workspacesList.value);

  const defaultStatus = computed(() => [
    { name: t('____x.new'), val: 'new' },
    { name: t('Approved'), val: 'confirm' },
    { name: t('Declined'), val: 'decline' },
    { name: t('Chargeback'), val: 'charge' },
    { name: t('Ignore conversion (not to be included in the stats)'), val: 'reject' },
  ]);

  const isActionsDisabled = computed(() => {
    if (mode !== 'edit') return false;
    return !!aclReadOnly({
      workspaceId: form.value.workspaceId,
      id: affiliateNetworkId.value,
    });
  });

  const showPlanLimitsWarning_tags = computed(
    () =>
      !!form.value.tags?.length
      && planLimitsControl.hasPlanLimitsRestriction('tagsAffiliateNetworks'),
  );

  const userTrackingDomain = computed(
    () => store.trackingDomainsObject[store.user.trackingDomainId] || {},
  );

  const domainForHttp = computed(() => userTrackingDomain.value.domain);

  const domainForHttps = computed(() => {
    if (userTrackingDomain.value.httpsAvailable) {
      return userTrackingDomain.value.domain;
    }

    const httpsDomain = store.trackingDomains.data.find(
      (item: { httpsAvailable: boolean }) => item.httpsAvailable,
    );
    return httpsDomain ? httpsDomain.domain : '';
  });

  const postbackUrl = computed(
    () =>
      `http://${domainForHttp.value}/postback?cid=${form.value.clickIdPlaceholder || 'REPLACE'}`
      + `&payout=${form.value.payoutPlaceholder || 'OPTIONAL'}`
      + `&txid=${form.value.transactionIdPlaceholder || 'OPTIONAL'}`
      + `&status=${form.value.statusPlaceholder || 'OPTIONAL'}`,
  );

  const securePostbackUrl = computed(
    () =>
      `https://${domainForHttps.value}/postback?cid=${form.value.clickIdPlaceholder || 'REPLACE'}`
      + `&payout=${form.value.payoutPlaceholder || 'OPTIONAL'}`
      + `&txid=${form.value.transactionIdPlaceholder || 'OPTIONAL'}`
      + `&status=${form.value.statusPlaceholder || 'OPTIONAL'}`,
  );

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const processForm = (source: AffiliateNetworkForm) => {
    const preparedForm = { ...source };
    preparedForm.tags = (preparedForm.tags || []).map((item) =>
      typeof item === 'object' && item && 'id' in item && (item as { id?: string }).id
        ? (item as { name: string }).name
        : item,
    );
    return preparedForm;
  };

  const applySubmitErrors = (error: unknown) => {
    const axiosError = error as { message?: string; response?: { status?: number } };
    if (
      axiosError.response?.status === 403
      || axiosError.response?.status === 400
      || axiosError.message === 'Network Error'
    ) {
      return;
    }
    if (axiosError.response?.status === 409 && showPlanLimitsErrorInToast(error)) {
      return;
    }
    if (!axiosError.response) {
      return;
    }

    applyApiErrors(error);
  };

  const applyTemplateFromApi = (data: Record<string, unknown>) => {
    if (data.templateId && data.affiliateNetworkTemplate) {
      const template = data.affiliateNetworkTemplate as Record<string, string>;
      const defaults = defaultAffiliateNetworkTemplate();
      form.value.templateId = data.templateId as string;
      form.value.clickIdPlaceholder =
        template.clickIdPlaceholder || defaults.clickIdPlaceholder;
      form.value.payoutPlaceholder = template.payoutPlaceholder || defaults.payoutPlaceholder;
      form.value.transactionIdPlaceholder =
        template.transactionIdPlaceholder || defaults.transactionIdPlaceholder;
      form.value.statusPlaceholder = template.statusPlaceholder || defaults.statusPlaceholder;
    } else {
      Object.assign(form.value, defaultAffiliateNetworkTemplate());
    }
  };

  const loadAffiliateNetworkFromApi = async (id: string) => {
    const data = await getAffiliateNetworkApiRequest(id);
    const fields =
      mode === 'edit' ? EDIT_AFFILIATE_NETWORK_FIELDS : DUPLICATE_AFFILIATE_NETWORK_FIELDS;
    form.value = pick(data, fields) as unknown as AffiliateNetworkForm;
    applyTemplateFromApi(data as Record<string, unknown>);
    return data;
  };

  const loadCreateForm = () =>
    reportForm.loadForm({
      defaultForm,
      form: form.value,
    });

  const loadData = async () => {
    const data = getData();

    if (mode === 'edit') {
      edit.value = true;
      affiliateNetworkId.value = data.id || (store.getTableSelectedItem().id as string);
      store.setLoading(true);
      clearErrors();
      try {
        await loadAffiliateNetworkFromApi(affiliateNetworkId.value);
        oldHash.value = getCurrentHash();
        show.value = true;
      } finally {
        store.setLoading(false);
      }
      return;
    }

    if (mode === 'duplicate') {
      store.setLoading(true);
      try {
        const sourceId = store.getTableSelectedItem().id as string;
        await loadAffiliateNetworkFromApi(sourceId);
        form.value.name = `${form.value.name} (${t('____x.copy')})`;
        affiliateNetworkId.value = '';
        oldHash.value = getCurrentHash();
        show.value = true;
      } finally {
        store.setLoading(false);
      }
      return;
    }

    store.setLoading(true);
    try {
      await loadCreateForm();
      if (!isEmpty(data)) {
        form.value = {
          ...form.value,
          ...data,
        } as AffiliateNetworkForm;
      }
      tutorial.form();
      oldHash.value = getCurrentHash();
      show.value = true;
    } finally {
      store.setLoading(false);
    }
  };

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash() && !isActionsDisabled.value) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const clearTags = () => {
    form.value.tags = [];
  };

  const trackSubmit = () => {
    const {
      name,
      usePostbackStatuses,
      acceptDoublePostbacks,
      postbackOnlyWhiteIps,
      postbackWhiteIps,
      hasCustomParamMasking,
    } = form.value;
    const eventName =
      mode === 'edit' ? 'affiliate_network_edit' : 'affiliate_network_create';
    trackMixpanel(eventName, {
      name,
      use_postback_statuses: !!usePostbackStatuses,
      accept_double_postbacks: !!acceptDoublePostbacks,
      postback_only_white_ips_enabled: !!postbackOnlyWhiteIps,
      has_custom_param_masking: !!hasCustomParamMasking,
      ...(postbackOnlyWhiteIps && { postback_white_ips: postbackWhiteIps }),
    });
  };

  const submit = async () => {
    store.setLoading(true);
    submitLoading.value = true;
    clearErrors();

    try {
      if (mode === 'edit') {
        const result = await putUpdateAffiliateNetworkApiRequest(
          affiliateNetworkId.value,
          processForm(form.value),
        );
        await refreshUserProfileFromApi();
        oldHash.value = getCurrentHash();
        reportUtilsApi.updateName('affiliate-networks', result.id as string);
        $emitter.emit('refresh-report');
        $emitter.emit('toast-success', t('Affiliate Network has been changed'));
        trackSubmit();
        close();
        return;
      }

      await postCreateAffiliateNetworkApiRequest(processForm(form.value));
      await refreshUserProfileFromApi();
      oldHash.value = getCurrentHash();
      $emitter.emit('refresh-report');
      $emitter.emit('toast-success', t('Affiliate Network has been saved'));
      trackSubmit();
      close();
    } catch (error) {
      applySubmitErrors(error);
    } finally {
      store.setLoading(false);
      submitLoading.value = false;
    }
  };

  onMounted(async () => {
    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }
    if (!store.cache.tags['affiliate-networks'].isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'affiliate-networks' });
    }
    await loadData();
  });

  return {
    store,

    t,
    show,
    step,
    submitLoading,
    form,
    errors,
    errorsControlActive,
    errorsDict,
    modalTitle,
    workspacesList,
    defaultStatus,
    isActionsDisabled,
    showPlanLimitsWarning_tags,
    postbackUrl,
    securePostbackUrl,
    hasPlanLimitsRestriction,
    tutorial,
    tutorialIntro,
    onChangeWorkspace: () => {},
    checkClose,
    clearTags,
    submit,
    getIntroParams: tutorialIntro.getIntroParams,
  };
};
